import React, { useEffect, useState } from 'react';
import { Slider, Typography, theme } from 'antd';
import { FILTER_ACCENT_COLOR, FILTER_INACTIVE_FILL } from '../constants/filterSliderTheme';

const { Text } = Typography;

interface PriceHistogramSliderProps {
  buckets: { min: number; max: number; count: number }[];
  range: [number, number];
  max: number;
  onChange: (range: [number, number]) => void;
  formatValue: (value: number) => string;
}

/**
 * Airbnb-style price picker: a bar-chart distribution of how many spaces
 * fall in each price bucket, sitting directly above a double-handle range
 * Slider on the same 0..max scale, so the bars and the handles always line
 * up column-for-column. Bars inside the selected range pick up the accent
 * color; bars outside it stay a flat neutral cream -- a real distribution
 * shape can legitimately have most of its buckets near zero (a floor
 * height keeps those visible instead of collapsing to an invisible sliver).
 *
 * Hovering a bar shows its range via a custom tooltip instead of the
 * native `title` attribute -- browsers delay `title` by several hundred ms
 * and it can't be styled, which read as sluggish here; this one is a plain
 * absolutely-positioned label with a near-instant opacity transition.
 *
 * `range` is the committed value (drives the actual search); `liveRange`
 * mirrors it but updates on every drag step so the handles/bars/labels
 * stay responsive while dragging -- the `onChange` prop (which the parent
 * wires to the real filter/search) only fires once, on release
 * (Slider's onChangeComplete), not per step.
 */
const PriceHistogramSlider: React.FC<PriceHistogramSliderProps> = ({ buckets, range, max, onChange, formatValue }) => {
  const { token } = theme.useToken();
  const maxCount = Math.max(1, ...buckets.map((b) => b.count));
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [liveRange, setLiveRange] = useState<[number, number]>(range);

  useEffect(() => {
    setLiveRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range[0], range[1]]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 64, marginBottom: 8 }}>
        {buckets.map((bucket, index) => {
          const inRange = bucket.max > liveRange[0] && bucket.min < liveRange[1];
          return (
            <div
              key={`${bucket.min}-${bucket.max}`}
              style={{ position: 'relative', flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end' }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex((current) => (current === index ? null : current))}
            >
              <div
                role="tooltip"
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginBottom: 6,
                  padding: '4px 8px',
                  borderRadius: 8,
                  background: token.colorText,
                  color: token.colorBgContainer,
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  opacity: hoveredIndex === index ? 1 : 0,
                  transition: 'opacity 50ms linear',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              >
                {formatValue(bucket.min)}–{formatValue(bucket.max)}: {bucket.count}
              </div>
              <div
                style={{
                  width: '100%',
                  height: `${Math.max(6, (bucket.count / maxCount) * 100)}%`,
                  background: inRange ? FILTER_ACCENT_COLOR : FILTER_INACTIVE_FILL,
                  borderRadius: 4,
                }}
              />
            </div>
          );
        })}
      </div>
      <Slider
        range
        className="heavyDutyMachinery-filter-slider"
        min={0}
        max={max}
        value={liveRange}
        tooltip={{ formatter: (value) => (value != null ? formatValue(value) : '') }}
        onChange={(value) => setLiveRange(value as [number, number])}
        onChangeComplete={(value) => onChange(value as [number, number])}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">{formatValue(liveRange[0])}</Text>
        <Text type="secondary">{formatValue(liveRange[1])}</Text>
      </div>
    </div>
  );
};

export default PriceHistogramSlider;
